/**
 * LocationForm — create/edit modal for a single location (address).
 *
 * Rendered by MapboxMain; the antd form instance and the acting mode
 * (state.action: "new" | "enter_address" | "enter_latlng") are owned by the
 * parent. The address text and the position are TWO SEPARATE CONTROLS:
 * AddressTextInput holds the text, AddressSearchInput asks Lantmäteriet and
 * returns only a coordinate. Searching therefore cannot rewrite the address —
 * which is what made the single-field version unusable for register rows named
 * "Fabriken" (GF-77, GF-65). Zone.jsx and SubZone.jsx still use AddressInput1
 * against Mapbox; that swap is a separate, unmeasured decision.
 * On update, `updatedInZone` is recomputed from
 * the zone circle so the marker turns green only when the new position is
 * inside the zone. Secondary fields collapse behind clickable labels.
 */
import React, { useState, useRef } from "react";
import { Form, Input, InputNumber, Spin, Row, Col, Popconfirm, Button, message } from "antd";
import { isEmpty } from "lodash";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import * as turf from "@turf/turf";

import "./Location.scss";
import AddressSearchInput from "../../Inputs/AddressSearchInput";
import AddressTextInput from "../../Inputs/AddressTextInput";
import { parseCoordinatePair } from "utils/coordinatePaste";
import { mergePickedPosition } from "utils/addressPick";
import { SubmitIcon, DeleteIcon } from "components/Icons";
import { PrimaryButton, TextButton } from 'components/Buttons';

import { doUpdateLocation, doCreateLocation, doDeleteLocation } from "modules/location/stores/thunk";
import { doDetailZone } from "modules/zone/stores/thunk";

const LocationForm = ({
  zone,
  circle,
  form,
  state,
  isFetching,
  handleCancel,
  onClosePopup,
}) => {
  const action = state.action;
  // const initialLat = form.getFieldValue("lat");
  // const initialLng = form.getFieldValue("lng");
  const dispatch = useDispatch();
  const [clickedLabels, setSlickedLabels] = useState([]);
  const customizeLabel = (label) => (
    <>
      {label}
      {clickedLabels.includes(label) ? (
        <CaretUpOutlined onClick={() => handleClickLabel(label)} />
      ) : (
        <CaretDownOutlined onClick={() => handleClickLabel(label)} />
      )}
    </>
  );
  const customizeClassName = (label, name) => (
    clickedLabels.includes(label) || form.getFieldValue(name) ? 'open' : 'close'
  );
  const handleClickLabel = (label) => {
    const clickedLabelsT = [...clickedLabels];
    if (clickedLabels.includes(label)) {
      setSlickedLabels(clickedLabelsT.filter((l) => l !== label));
    } else {
      clickedLabelsT.push(label);
      setSlickedLabels(clickedLabelsT);
    }
  };

  const handleDelete = async () => {
    await dispatch(doDeleteLocation(state.location._id));
    handleCancel();
    onClosePopup();
    await dispatch(doDetailZone(zone._id));
  };

  const handleSubmit = async (values) => {
    const params = { ...values };
    if (values.location) {
      params.address = values.location.address;
      // if (
      //   params.lat != initialLat || !initialLat || !params.lat ||
      //   params.lng != initialLng || !initialLng || !params.lng
      // ) {
      // }
      // params.lat = values.location.latLng.lat;
      // params.lng = values.location.latLng.lng;
    } else {
      params.address = values.currentAddress;
    }
    if (state.action === 'new') {
      await dispatch(doCreateLocation({ ...params, zone: zone._id }));
    } else {
      const point = turf.point([params.lng, params.lat]);
      const polygon = turf.polygon([...circle.geometry.coordinates]);
      params.updatedInZone = turf.booleanPointInPolygon(point, polygon);
      await dispatch(doUpdateLocation({ id: state.location._id, ...params }));
    }
    handleCancel();
    onClosePopup();
    if (state.action === 'new') {
      await dispatch(doDetailZone(zone._id));
    }
  };

  let ownerInputRef = useRef(null);

  // Paste interceptor: runs the clipboard text through `mutator` and inserts
  // it at the caret manually. Used to normalize text pasted from the source
  // register (capitalization) without altering what the user typed by hand.
  function modifyClipboard(field, mutator) {
    return e => {
      if (!window.getSelection().rangeCount) return false;
      let clipboardData = (e.clipboardData || window.clipboardData);
      let newData = mutator(clipboardData.getData("text"));
      let oldValue = form.getFieldValue(field) || "";
      let preLen = e.target.selectionStart;
      form.setFieldValue(field, oldValue.substr(0, preLen) + newData + oldValue.substr(e.target.selectionEnd));
      preLen += newData.length;
      let target = e.target;
      setTimeout(() => { // Wait until next event-cycle.
        target.selectionStart = preLen;
        target.selectionEnd = preLen;
      }, 0)
      e.preventDefault();
    }
  }
  // /**
  //  * @param {ClipboardEvent} e 
  //  */
  // function onPasteCapitalizeWords(e) { // fattar vad han menar
  //   if (!window.getSelection().rangeCount) return false;
  //   let clipboardData = (e.clipboardData || window.clipboardData);
  //   let newData = clipboardData.getData("text").toUpperCase();
  //   let oldValue = form.getFieldValue("owner");
  //   let preLen = e.target.selectionStart;
  //   form.setFieldValue("owner", oldValue.substr(0, preLen) + newData + oldValue.substr(e.target.selectionEnd));
  //   preLen += newData.length;
  //   let target = e.target;
  //   setTimeout(() => { // Wait until next event-cycle.
  //     target.selectionStart = preLen;
  //     target.selectionEnd = preLen;
  //   }, 0)
  //   e.preventDefault();
  // }
  function onPasteCapitalizeWords(field) { // fattar vad han menar
    return modifyClipboard(field, v => v.split(" ").map(v => String(v).charAt(0).toUpperCase() + String(v).slice(1)).join(" "));
  }
  function onPasteCapitalizeAll(field) { // fattar vad han menar
    return modifyClipboard(field, v => v.toUpperCase());
  }

  /**
   * Klistrar man in ett helt koordinatpar i Lat- ELLER Long-rutan delas det
   * upp över båda. Paret ligger redan i urklipp när man hämtat en position
   * från hitta.se eller Google, och att skriva av det för hand i två fält är
   * onödigt felkänsligt.
   *
   * Går strängen inte att tolka entydigt gör vi INGENTING och låter
   * webbläsaren klistra in som vanligt — då fungerar det fortfarande att
   * klistra in ett enda tal i ett enda fält. Reglerna bor i
   * utils/coordinatePaste.js.
   */
  function onPasteCoordinates(e) {
    const clipboardData = e.clipboardData || window.clipboardData;
    const par = parseCoordinatePair(clipboardData.getData("text"));
    if (!par) {
      return; // ingen preventDefault: normal inklistring
    }
    e.preventDefault();
    form.setFieldsValue({ lat: par.lat, lng: par.lng });
    // setFieldsValue utlöser INTE omvalideringen som `dependencies` på
    // adressfältet bygger på — rc-field-form kör den bara när ett fält ändras
    // genom sin egen onChange. Utan det här anropet står adressfältet kvar
    // rött efter en lyckad inklistring, och den som ser rött skriver rimligen
    // om adressen, vilket stänger just den väg GF-34-regeln öppnade.
    form.validateFields(["location"]).catch(() => {});
    if (par.swapped) {
      // Vi rättar inte åt användaren — vi vet inte vad hen menade. Men att
      // tiga vore att låta en position hamna i fel världsdel utan ett ord.
      message.warning(
        `Talen ser omkastade ut: ${par.lat} som latitud hamnar utanför Sverige. Kontrollera ordningen.`,
      );
    }
  }

  return (
    <Spin spinning={isFetching}>
      <div className="LocationForm">
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleSubmit}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <div className="FormHeader">
            <div className="left">
              {action === 'new' ? "New Address" : action === 'enter_latlng' ? "Redigera Lat/long" : "Redigera adress"}
            </div>
          </div>
          <div className="FormContent">
            {action !== 'new' && (
              <Form.Item
                label="Nuvarande Adress"
                name="currentAddress"
                style={{ width: "100%" }}
              >
                <Input
                  size="large"
                  disabled
                />
              </Form.Item>
            )}
            <Form.Item
              label="Uppdaterad adress"
              name="location"
              // Re-validate when Lat/Long are typed, since the rule reads them.
              dependencies={["lat", "lng"]}
              rules={[
                {
                  validator: (_, value) => {
                    if (isEmpty(value?.address)) {
                      return Promise.reject(new Error("Required"));
                    }
                    // Coordinates normally come from the autocomplete pick
                    // (value.latLng). GF-34: an address that was never
                    // geocoded opens this form with latLng null (it used to
                    // carry the Baltic dummy point, which this rule accepted
                    // and a bare "Bekräfta" wrote to the database). Typing
                    // coordinates into the Lat/Long fields below — which are
                    // what handleSubmit actually persists — must stay a valid
                    // way through; it is the only way to fix exactly those
                    // addresses. It counts ONLY while the address text is
                    // unchanged: a rewritten address still requires a pick,
                    // exactly as before, so free text never gets saved on top
                    // of stale coordinates.
                    const picked = value?.latLng?.lat && value?.latLng?.lng;
                    const typed = form.getFieldValue("lat") && form.getFieldValue("lng");
                    const textUnchanged = value?.address === form.getFieldValue("currentAddress");
                    if (picked || (typed && textUnchanged)) {
                      return Promise.resolve();
                    }
                    // THE MESSAGE SAYS WHAT TO DO, because this rejection is
                    // now easy to reach and impossible to guess. Editing the
                    // address text clears the picked position by design — a
                    // rewritten address must not be saved on top of
                    // coordinates chosen for a different one — so the natural
                    // order "pick position, then tidy the text" lands exactly
                    // here. It said "Invalid", which named the state and not
                    // the way out.
                    return Promise.reject(
                      new Error(
                        "Adressen är ändrad. Sök fram positionen igen, eller skriv in Lat och Long för hand."
                      )
                    );
                  },
                },
              ]}
            >
              <AddressTextInput size="large" placeholder="Uppdaterad adress" />
            </Form.Item>
            {/* A SEPARATE CONTROL, WITH NO `name` — so the query is not part of
                the form's values and can never be saved as the address. That
                separation is the fix: one box cannot be both "what I am looking
                for" and "what will be stored". See AddressSearchInput. */}
            {/* ⚠️ NOT IN "new" MODE, AND THE REASON IS NOT COSMETIC.
                Measured 2026-09-18 (src/utils/newModeFormValues.test.js):
                rc-field-form resolves onFinish from REGISTERED fields only, and
                this mode renders no Lat/Long inputs — so a position picked here
                was dropped in silence while the box reported success. Not a
                regression; the old Mapbox box lost it the same way. The lie on
                screen was new.

                Removing the box rather than registering the fields is the
                smaller and the truer change: this control exists for addresses
                where automatic geocoding has already given up, which cannot
                have happened to an address that does not exist yet. A new
                address is saved and then geocoded by the pipeline — with
                Lantmäteriet, the stronger source. Letting an operator hand-place
                a position at creation is a separate decision with its own
                consequences for updatedInZone, the review candidates and what
                the Geokoda button then does with the row. */}
            {action !== 'new' && (
            <Form.Item label="Sök position i Lantmäteriet" style={{ width: "100%" }}>
              <AddressSearchInput
                // REMOUNT PER ADDRESS ROW. This modal is NOT destroyOnClose —
                // the ConfirmAddress modal 30 lines below in Main.jsx is, with a
                // measured GF-63 comment explaining why. antd keeps a closed
                // modal's body mounted, so without this key the search box would
                // carry its own useState into the NEXT address the operator
                // opens: row A's query still in the box, row A's result list
                // still open and clickable, and row A's green confirmation line
                // showing before anything was done. A click would then write row
                // A's coordinate onto row B.
                //
                // AddressInput1 was immune to this by accident — its
                // useEffect([value]) re-synced from the form value on every
                // opening. This control deliberately owns no form field, so it
                // has no such way back. Found by review 2026-09-17.
                //
                // ⚠️ THE ACTION IS IN THE KEY, AND IT IS NOT DECORATION. Keying
                // on the row id alone was the first fix and it left a hole:
                // handleClickAction sets location:null for "new", so two "New
                // Address" openings in a row both key on "new" and nothing
                // remounts — the previous search, its open result list and its
                // green confirmation line would all still be there, on an empty
                // form. handleCancel sets action:"" on every close, so
                // including it makes the key flip on every open/close cycle
                // whatever the id is. That is the invariant actually wanted;
                // "the rows have different ids" was a proxy for it, and a
                // missing or non-string _id would have broken the proxy too.
                // Second review pass, 2026-09-17.
                key={`${state.action}:${state.location?._id ?? "new"}`}
                size="large"
                // Prefilled with the address so the operator does not retype it.
                // Editing the query from here changes NOTHING but the search —
                // that is the whole difference from the previous version.
                defaultQuery={state.location?.address ?? ""}
                onSelect={(position) => {
                  // The merge rule — "the pick sets the position and nothing
                  // else" — lives in utils/addressPick.js so that it can be
                  // guarded without rendering anything. See its tests.
                  form.setFieldsValue(
                    mergePickedPosition(form.getFieldValue("location"), position)
                  );
                  // setFieldsValue does NOT re-run validation — the same reason
                  // onPasteCoordinates above calls this. Without it the address
                  // field stays red, telling the operator to search for the
                  // position again immediately after they did exactly that.
                  form.validateFields(["location"]).catch(() => {});
                }}
              />
            </Form.Item>
            )}
            {action !== 'new' && (
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item
                    label="Lat"
                    name="lat"
                    rules={[
                      { required: true, message: "Required" }, 
                      {
                        pattern: /^(\+|-)?(?:90(?:(?:\.0+)?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]+)?))$/,
                        message: 'Invalid',
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Lat"
                      style={{ width: "100%" }}
                      onPaste={onPasteCoordinates}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Long"
                    name="lng"
                    rules={[
                      { required: true, message: "Required" }, 
                      {
                        pattern: /^(\+|-)?(?:180(?:(?:\.0+)?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]+)?))$/,
                        message: 'Invalid',
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Long"
                      style={{ width: "100%" }}
                      onPaste={onPasteCoordinates}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            {/*<Form.Item
              label="Note"
              name="note"
            >
              <Input
                size="large"
                placeholder="Note"
              />
            </Form.Item>*/}
            <Form.Item
              className={customizeClassName('Ägare', 'owner')}
              label={customizeLabel('Ägare')}
              name="owner"
            >
              <Input
                size="large"
                placeholder="Ägare"
                onPaste={onPasteCapitalizeWords("owner")}
                ref={ownerInputRef}
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Nummer', 'ownerNumber')}
              label={customizeLabel('Nummer')}
              name="ownerNumber"
            >
              <Input
                size="large"
                placeholder="Nummer"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Fastighetsbeteckning', 'propertyDesignation')}
              label={customizeLabel('Fastighetsbeteckning')}
              name="propertyDesignation"
            >
              <Input
                size="large"
                placeholder="Fastighetsbeteckning"
                onPaste={onPasteCapitalizeAll("propertyDesignation")}
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Fastighetsnotering', 'realEstateListing')}
              label={customizeLabel('Fastighetsnotering')}
              name="realEstateListing"
            >
              <Input
                size="large"
                placeholder="Fastighetsnotering"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Arbetsorderflaggning', 'workOrderFlag')}
              label={customizeLabel('Arbetsorderflaggning')}
              name="workOrderFlag"
            >
              <Input
                size="large"
                placeholder="Arbetsorderflaggning"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Registreringsnummer', 'registrationNumber')}
              label={customizeLabel('Registreringsnummer')}
              name="registrationNumber"
            >
              <Input
                size="large"
                placeholder="Registreringsnummer"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Ny adress', 'freeTextField1')}
              label={customizeLabel('Ny adress')}
              name="freeTextField1"
            >
              <Input
                size="large"
                placeholder="Ny adress"
                onPaste={onPasteCapitalizeWords("freeTextField1")}
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Övriga noteringar 2', 'freeTextField2')}
              label={customizeLabel('Övriga noteringar 2')}
              name="freeTextField2"
            >
              <Input
                size="large"
                placeholder="Övriga noteringar 2"
              />
            </Form.Item>
          </div>
          <div className="FormFooter">
            <div className="Left">
              {action !== 'new' && (
                <Popconfirm
                  title="Radera adress"
                  description="Är du säker på att du vill radera denna adress?"
                  onConfirm={handleDelete}
                  okText="Ja"
                  cancelText="Nej"
                >
                  <Button className="delete" danger><DeleteIcon /></Button>
                </Popconfirm>
              )}
            </div>
            <div className="Right">
              <TextButton
                key="cancel"
                label="Avbryt"
                onClick={handleCancel}
              />
              <PrimaryButton
                htmlType="submit"
                type="primary"
                label="Bekräfta"
                icon={<SubmitIcon />}
              />
            </div>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default LocationForm;
